import { useEffect, useId } from 'react'
import { useBlocker } from 'react-router-dom'
import { create } from 'zustand'

interface DirtyStore {
  keys: Set<string>
  setKey: (key: string, dirty: boolean) => void
}

const useDirtyStore = create<DirtyStore>((set) => ({
  keys: new Set(),
  setKey: (key, dirty) =>
    set((s) => {
      const next = new Set(s.keys)
      if (dirty) next.add(key)
      else next.delete(key)
      return { keys: next }
    }),
}))

// Called by individual form components — registers their dirty state.
export function useUnsavedChangesGuard(isDirty: boolean) {
  const id = useId()
  const setKey = useDirtyStore((s) => s.setKey)

  useEffect(() => {
    setKey(id, isDirty)
    return () => setKey(id, false)
  }, [id, isDirty, setKey])
}

// Called once in Shell — holds the single useBlocker and beforeunload handler.
export function useUnsavedChangesBlocker() {
  const isDirty = useDirtyStore((s) => s.keys.size > 0)
  const blocker = useBlocker(isDirty)

  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (confirm('you have unsaved changes. leave anyway?')) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker])

  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault() }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])
}
